// components/BarLoader.jsx
import React, { useState, useEffect } from 'react';
import { FileUp, Download, CheckCircle, AlertCircle, Loader2, Sparkles } from 'lucide-react';

const BarLoader = ({ message, type = 'upload', currentProgress = null }) => {
  const [internalProgress, setInternalProgress] = useState(0);
  const [dots, setDots] = useState('');
  const [sparklePositions, setSparklePositions] = useState([]);

  useEffect(() => {
    const generateSparkles = () => {
      const positions = [];
      for (let i = 0; i < 8; i++) {
        positions.push({
          x: Math.random() * 100,
          y: Math.random() * 100,
          delay: Math.random() * 2,
          duration: 1 + Math.random() * 2
        });
      }
      setSparklePositions(positions);
    };
    generateSparkles();
  }, []);

  useEffect(() => {
    if (currentProgress !== null) {
      setInternalProgress(currentProgress);
    } else {
      const interval = setInterval(() => {
        setInternalProgress((prev) => {
          if (prev >= 100) return 0;
          return prev + Math.random() * 15;
        });
      }, 300);
      return () => clearInterval(interval);
    }
  }, [currentProgress]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) return '';
        return prev + '.';
      });
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const getIcon = () => {
    switch (type) {
      case 'upload':
        return <FileUp className="w-8 h-8 text-purple-500" />;
      case 'download':
        return <Download className="w-8 h-8 text-orange-500" />;
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-8 h-8 text-red-500" />;
      default:
        return <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />;
    }
  };

  const getGradientColors = () => {
    return 'from-[#2a52be] to-[#1e3a8a]';
  };

  return (
    <div className="relative">
      <div className="absolute inset-0 pointer-events-none">
        {sparklePositions.map((sparkle, index) => (
          <div
            key={index}
            className="absolute w-2 h-2 text-yellow-400 opacity-60"
            style={{
              left: `${sparkle.x}%`,
              top: `${sparkle.y}%`,
              animationDelay: `${sparkle.delay}s`,
              animationDuration: `${sparkle.duration}s`
            }}
          >
            <Sparkles className="w-full h-full animate-pulse" />
          </div>
        ))}
      </div>

      <div className="flex flex-col items-center justify-center p-8 bg-gradient-to-br from-white via-gray-50 to-white rounded-3xl shadow-2xl border border-gray-200/50 backdrop-blur-xl max-w-lg mx-auto text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-200 to-transparent animate-pulse"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(99,102,241,0.1),transparent)] animate-pulse"></div>
        </div>

        <div className="relative mb-6 p-4 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full shadow-lg animate-bounce">
          <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent rounded-full"></div>
          <div className="relative z-10 animate-pulse">
            {getIcon()}
          </div>
          <div className="absolute inset-0 rounded-full animate-ping opacity-20 bg-gradient-to-r from-blue-400 to-purple-400"></div>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-3 mb-6 overflow-hidden shadow-inner relative">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-[shimmer_2s_infinite] w-full"></div>
          <div
            className={`h-full rounded-full bg-gradient-to-r ${getGradientColors()} relative overflow-hidden transition-all duration-300 ease-out shadow-lg`}
            style={{ width: `${Math.min(internalProgress, 100)}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[slide_1.5s_infinite] -translate-x-full"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
          </div>
        </div>

        <div className="mb-4 min-h-[2rem] flex items-center justify-center">
          <p className="text-xl font-bold text-gray-800 animate-pulse">
            {message}
            {currentProgress !== null && <span className="ml-2">{Math.round(internalProgress)}%</span>}
            <span className="text-blue-500 ml-1 animate-pulse">{dots}</span>
          </p>
        </div>

        <p className="text-sm text-gray-600 animate-fade-in-up opacity-70">
          Please wait, this may take a moment...
        </p>

        <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-br from-pink-400 to-orange-400 rounded-full opacity-20 animate-pulse animation-delay-1000"></div>
      </div>
    </div>
  );
};

export default BarLoader;